import type { UiKey } from './i18n/en';

type FooterEntry = { label: string; href: string; key?: UiKey };

export const images = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/99400ed7-5fcc-4af4-8233-a7ea5a6deb41.jpg',
  film: 'https://cdn.magicpatterns.com/patterns/generated-images/ebb099e8-f1b2-42ad-99ef-ce50d27a7090.jpg',
  press: 'https://cdn.magicpatterns.com/patterns/generated-images/58ead860-2668-40e2-925d-9505e7a82056.jpg',
  events: 'https://cdn.magicpatterns.com/patterns/generated-images/1637065a-fb6c-4924-a046-118a939648f2.jpg',
  reputation: 'https://cdn.magicpatterns.com/patterns/generated-images/ad98ae4d-773e-42ac-b93b-cdd18e60fd76.jpg',
};

// Drop a hosted .mp4 URL here to replace the cinematic still sequence with a real film.
// Hero film — loaded once the page has appeared; the stills hold the frame until it can play
export const heroVideo =
  'https://s3.amazonaws.com/webflow-prod-assets/67dd5fcccda09c76d473a2ef/67dd5fcccda09c76d473a364_Portada%20Video.mp4';

export const heroScenes = [
  {
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/64b5c47e-50cb-4347-9ca0-a8be7ce41661.jpg',
    alt: 'A man in a linen suit on a villa terrace above Monaco harbour at sunset',
    caption: 'Monaco, above the harbour — 20:10',
    // Optional: hosted .mp4 clip for this scene. Leave empty to use the animated still.
    video: '',
  },
  {
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1248908f-9a3c-4b72-9268-25a593c91aa7.jpg',
    alt: 'A woman in an evening dress on a superyacht deck facing the Dubai skyline at blue hour',
    caption: 'Dubai, offshore — 19:40',
    video: '',
  },
  {
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/2ab0df89-c155-4e3d-a23e-ace1c5c7201c.jpg',
    alt: 'A couple dining on the terrace of a clifftop villa in Mallorca at sunset',
    caption: 'Mallorca, Tramuntana coast — 20:45',
    video: '',
  },
  {
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1beb1701-7252-4dce-a6f7-d5d7721afadc.jpg',
    alt: 'A table for two in a fine dining room above the lit Dubai skyline',
    caption: 'Dubai, a private dining room — 22:15',
    video: '',
  },
  {
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/fb08e70a-d246-4c51-978c-5505080db34b.jpg',
    alt: 'A timber and glass chalet in the Alps at dusk with snow-covered peaks',
    caption: 'The Alps, a private chalet — 17:30',
    video: '',
  },
];

export const headerNavLeft: { label: string; key: UiKey; href: string }[] = [
  { label: 'Residences', key: 'nav.residences', href: '/residences' },
  { label: 'Mobility', key: 'nav.mobility', href: '/mobility' },
  { label: 'Retreats', key: 'nav.retreats', href: '/retreats' },
  { label: 'SL Club', key: 'nav.slClub', href: '/sl-club' },
];

export const headerNavRight: { label: string; key: UiKey; href: string }[] = [
  { label: 'Private Line', key: 'nav.privateLine', href: '/private-line' },
  { label: 'Membership', key: 'nav.membership', href: '/membership' },
  { label: 'Login', key: 'nav.login', href: '/login' },
];

export const announcement = {
  text: 'Private consultations are now received in our new Geneva and Dubai ateliers.',
  linkLabel: 'Request a private audience',
  href: '/membership',
};

export const serviceAreas = [
  {
    title: 'Residences',
    stat: '240+ private residences',
    description: 'Villas, penthouses and estates — offered privately, before they are ever listed.',
    image: 'https://cdn.prod.website-files.com/67dd5fcccda09c76d473a2ef/6a46244f00da01c94a66db75_Group%201000002247%20(2).avif',
    alt: 'Residences — a private Superluxury residence',
    href: '/residences',
  },
  {
    title: 'Mobility',
    stat: '180+ cars, yachts & jets',
    description: 'Cars, yachts and jets — sourced, chartered or acquired with complete discretion.',
    image: 'https://cdn.prod.website-files.com/67dd5fcccda09c76d473a2ef/6a462589274fe941657f0d13_Rectangle%20923.avif',
    alt: 'Mobility — private cars, yachts and aviation by Superluxury',
    href: '/mobility',
  },
  {
    title: 'Retreats',
    stat: '60+ hideaways worldwide',
    description: 'Secluded sanctuaries for rest, wellness and quiet — held for you alone.',
    image: 'https://cdn.prod.website-files.com/67dd5fcccda09c76d473a2ef/6a4624c8caf1fc2344615890_Rectangle%20921.avif',
    alt: 'Retreats — a secluded Superluxury retreat',
    href: '/retreats',
  },
  {
    title: 'SL Club',
    stat: '120+ private events a year',
    description: 'A private members’ club — kept small, kept quiet, open only by introduction.',
    image: 'https://cdn.prod.website-files.com/67dd5fcccda09c76d473a2ef/6a462542756b4af58325f77b_Rectangle%20922.avif',
    alt: 'SL Club — members gathered at a private Superluxury club setting',
    href: '/sl-club',
  },
];

export const conciergeExtras = [
  { label: 'Private Chauffeur', icon: 'car', href: '#consultation' },
  { label: 'Restaurant Tables', icon: 'utensils', href: '#consultation' },
  { label: 'Private Aviation', icon: 'plane', href: '#consultation' },
  { label: 'Security & Discretion', icon: 'shield', href: '#consultation' },
  { label: 'All Concierge Services', icon: 'plus', href: '#editorial' },
] as const;

export { socialProfiles as socialLinks } from './contact';

export type DestinationSeason = 'year-round' | 'seasonal';

export const destinations: {
  city: string;
  season: DestinationSeason;
  availability: string;
  focus: string;
  country: string;
  timeZone: string;
  image: string;
  alt: string;
}[] = [
  {
    city: 'Dubai',
    season: 'year-round',
    availability: 'Year-round',
    focus: 'Penthouses, marina berths, desert retreats',
    country: 'United Arab Emirates',
    timeZone: 'Asia/Dubai',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e85650ec-bfdf-4f7c-b2b3-0b278605385e.jpg',
    alt: 'The Dubai skyline at golden hour behind a marina with a superyacht',
  },
  {
    city: 'Monaco',
    season: 'year-round',
    availability: 'Year-round',
    focus: 'Grand Prix suites, yacht charters, private tables',
    country: 'Principality of Monaco',
    timeZone: 'Europe/Monaco',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/fa42b9b4-723d-4811-b9c0-5315d8da5887.jpg',
    alt: 'Monaco harbour filled with superyachts beneath the terraced hillside',
  },
  {
    city: 'Ibiza',
    season: 'seasonal',
    availability: 'May — October',
    focus: 'Cliffside villas, closed-door evenings, boats',
    country: 'Balearic Islands, Spain',
    timeZone: 'Europe/Madrid',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c5f748cf-11a8-43a6-b1b7-2a88652d7087.jpg',
    alt: 'A whitewashed cliffside villa with an infinity pool at sunset in Ibiza',
  },
  {
    city: 'Hamburg',
    season: 'year-round',
    availability: 'Year-round',
    focus: 'Private residences, harbour launches, premieres',
    country: 'Germany',
    timeZone: 'Europe/Berlin',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/fb6424d9-3a72-49d6-927e-4cdf59e39b14.jpg',
    alt: 'Hamburg harbour at blue hour with the concert hall and brick warehouses',
  },
  {
    city: 'Mallorca',
    season: 'seasonal',
    availability: 'April — October',
    focus: 'Fincas, sailing yachts, secluded calas',
    country: 'Balearic Islands, Spain',
    timeZone: 'Europe/Madrid',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a1eb3c2d-0db6-4eaf-9098-ebf3779a4938.jpg',
    alt: 'A wooden sailing yacht anchored in a turquoise cove in Mallorca',
  },
  {
    city: 'Warsaw',
    season: 'year-round',
    availability: 'Year-round',
    focus: 'Private dining, cultural access, chauffeurs',
    country: 'Poland',
    timeZone: 'Europe/Warsaw',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/aa046399-311a-4b14-b653-845623fd944b.jpg',
    alt: 'Warsaw Old Town square at golden hour with café tables',
  },
  {
    city: 'Menorca',
    season: 'seasonal',
    availability: 'June — September',
    focus: 'Quiet coves, estate rentals, island hopping',
    country: 'Balearic Islands, Spain',
    timeZone: 'Europe/Madrid',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e7740d17-378f-4c4d-b93b-2056ce829695.jpg',
    alt: 'A secluded white-sand cove with turquoise water in Menorca',
  },
];

export const footerColumns: { title: string; titleKey: UiKey; links: FooterEntry[] }[] = [
  {
    title: 'May We Help You?',
    titleKey: 'footer.help',
    links: [
      { label: 'Private Line', key: 'nav.privateLine', href: 'tel:+971505254282' },
      { label: 'hey@superluxury.io', href: 'mailto:hey@superluxury.io' },
      { label: 'WhatsApp', href: 'https://wa.me/971505254282' },
      { label: 'Frequently Asked Questions', key: 'footer.faq', href: '#faq' },
      { label: 'Support: English', key: 'footer.support', href: 'mailto:hey@superluxury.io' },
    ],
  },
  {
    title: 'The House',
    titleKey: 'footer.house',
    links: [
      { label: 'Residences', key: 'nav.residences', href: '/residences' },
      { label: 'Mobility', key: 'nav.mobility', href: '/mobility' },
      { label: 'Retreats', key: 'nav.retreats', href: '/retreats' },
      { label: 'SL Club', key: 'nav.slClub', href: '/sl-club' },
    ],
  },
];

export const footerLegal = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Cookie Policy', href: '/cookie-policy' },
  { label: 'Imprint', href: '/imprint' },
  { label: 'Cookie Settings', href: '#cookie-settings' },
];

export const footerServices: { title: string; titleKey: UiKey; links: FooterEntry[] } = {
  title: 'Superluxury Services',
  titleKey: 'footer.services',
  links: [
    { label: 'Discover Our Services', key: 'footer.discoverServices', href: '#editorial' },
    { label: 'Seek an Introduction', key: 'footer.seekIntro', href: '/membership' },
    { label: 'Destinations', key: 'footer.destinations', href: '#destinations' },
  ],
};

// Brand account names are never translated
export const footerSocial: { title: string; titleKey: UiKey; links: FooterEntry[] } = {
  title: 'Follow Us',
  titleKey: 'footer.follow',
  links: [
    { label: 'Superluxury Media', href: 'https://www.instagram.com/superluxurymedia/' },
    { label: 'Superluxury Retreats', href: 'https://www.instagram.com/superluxuryretreats/' },
    { label: 'Superluxury Dubai', href: 'https://www.instagram.com/superluxury.dubai?dlrf=MW9ncGM2c3lkaWhtcA==' },
    { label: 'Superluxury Mallorca', href: 'https://www.instagram.com/superluxurymallorca/' },
    { label: 'SL Club', href: '/sl-club' },
    { label: 'Threads', href: 'https://www.threads.com/@superluxurymedia' },
  ],
};
